import React, { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Divider, IconButton, Tooltip, Typography } from '@mui/material';
import ErrorHandlingLayout from '@sharedCR/common/ErrorHandlingLayout';
import PageBlock from '@sharedCR/common/PageBlock';
import { RefreshIcon } from '@sharedCR/coreDesignSystem/icons';
import { TableCDS } from '@sharedCR/coreDesignSystem/Table';
import Skeleton from 'app/shared/components/common/Skeleton';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import { SKELETON_TYPES } from 'constants/skeleton';
import { SnapdocsClosingLoadState, SnapdocsClosingResource } from 'constants/snapdocsClosing';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import { getSnapdocsClosingAction } from 'store/snapdocsClosing/actions';
import { selectSnapdocsClosing } from 'store/snapdocsClosing/selectors';

import FieldRow from '../FieldRow';
import IconGroupPanel from '../IconGroupPanel';

import SnapdocsAuditTrailBlock from './components/SnapdocsAuditTrailBlock';
import SnapdocsClosingBlock from './components/SnapdocsClosingBlock';
import SnapdocsCommentsBlock from './components/SnapdocsCommentsBlock';
import SnapdocsQualityControlBlock from './components/SnapdocsQualityControlBlock';
import {
  borrowersTableConfig,
  getDetailGroupsConfig,
  getSummaryRowsConfig,
} from './SnapdocsClosingSection.config';
import { compactRowConfig, SECTION_TITLE, SKELETON_ROWS } from './SnapdocsClosingSection.constants';
import {
  blocksGridStyles,
  compactRowLabelStyles,
  compactRowStyles,
  errorContentStyles,
  summaryGridStyles,
} from './SnapdocsClosingSection.styles';
import { SnapdocsClosingSectionTestIds } from './SnapdocsClosingSection.testIds';

const SnapdocsClosingSection = ({ applicationId, companyId }) => {
  const dispatch = useDispatch();
  const {
    applicationId: loadedApplicationId,
    loadState: storedLoadState,
    errorMessage,
    status,
    appointment,
    borrowers,
    notaryOrder,
    qualityControl,
    comments,
    blockErrors,
  } = useSelector(selectSnapdocsClosing);

  const loadState =
    loadedApplicationId === applicationId ? storedLoadState : SnapdocsClosingLoadState.LOADING;

  const summaryRows = useMemo(
    () => getSummaryRowsConfig({ status, appointment, notaryOrder }),
    [status, appointment, notaryOrder],
  );

  const detailGroups = useMemo(
    () =>
      getDetailGroupsConfig({
        status,
        appointment,
        notaryOrder,
        notaryErrorMessage: blockErrors[SnapdocsClosingResource.NOTARY],
      }),
    [status, appointment, notaryOrder, blockErrors],
  );

  const fetchClosing = useCallback(() => {
    dispatch(getSnapdocsClosingAction({ applicationId, companyId }));
  }, [dispatch, applicationId, companyId]);

  useEffectSafeStrictMode(() => {
    fetchClosing();
  }, [fetchClosing]);

  const refreshButton = (
    <Tooltip title="Refresh">
      <span>
        <IconButton
          aria-label="Refresh SnapDocs closing"
          data-testid={SnapdocsClosingSectionTestIds.REFRESH_BUTTON}
          disabled={loadState === SnapdocsClosingLoadState.LOADING}
          onClick={fetchClosing}
        >
          <RefreshIcon size={IconSize[20]} />
        </IconButton>
      </span>
    </Tooltip>
  );

  const compactRow = compactRowConfig[loadState];

  if (compactRow) {
    return (
      <Box data-testid={compactRow.dataTestId} sx={compactRowStyles}>
        <Box sx={compactRowLabelStyles}>
          <Typography variant="medium16">{SECTION_TITLE}</Typography>
          <Typography color="text.secondary" variant="regular14">
            {compactRow.message}
          </Typography>
        </Box>
        {refreshButton}
      </Box>
    );
  }

  const getContent = () => {
    if (loadState === SnapdocsClosingLoadState.LOADING) {
      return (
        <Box data-testid={SnapdocsClosingSectionTestIds.LOADING_SKELETON}>
          <Skeleton height={40} length={SKELETON_ROWS} type={SKELETON_TYPES.LIST} />
        </Box>
      );
    }

    if (loadState === SnapdocsClosingLoadState.ERROR) {
      return (
        <ErrorHandlingLayout
          isError
          dataTestId={SnapdocsClosingSectionTestIds.RETRY_BUTTON}
          onClick={fetchClosing}
        >
          <Box sx={errorContentStyles}>
            <Typography
              color="error.main"
              data-testid={SnapdocsClosingSectionTestIds.ERROR_MESSAGE}
              variant="regular14"
            >
              {errorMessage}
            </Typography>
          </Box>
        </ErrorHandlingLayout>
      );
    }

    return (
      <>
        <Box data-testid={SnapdocsClosingSectionTestIds.SUMMARY} sx={summaryGridStyles}>
          {summaryRows.map(({ id, label, value }) => (
            <FieldRow key={id} label={label} value={value} />
          ))}
        </Box>

        <Divider />

        <Box data-testid={SnapdocsClosingSectionTestIds.DETAILS_PANEL}>
          <IconGroupPanel groups={detailGroups} />
        </Box>

        <Divider />

        <SnapdocsClosingBlock
          dataTestId={SnapdocsClosingSectionTestIds.BORROWERS_TABLE}
          emptyMessage="No borrowers on this closing"
          errorMessage={blockErrors[SnapdocsClosingResource.BORROWERS]}
          isEmpty={!borrowers.length}
          title="Borrowers"
        >
          <TableCDS config={borrowersTableConfig} data={borrowers} />
        </SnapdocsClosingBlock>

        <Divider />

        <Box sx={blocksGridStyles}>
          <SnapdocsQualityControlBlock
            errorMessage={blockErrors[SnapdocsClosingResource.QUALITY_CONTROL]}
            fallbackStatus={status?.qcStatus}
            qualityControl={qualityControl}
          />
          <SnapdocsCommentsBlock
            commentCount={status?.commentCount}
            comments={comments}
            errorMessage={blockErrors[SnapdocsClosingResource.COMMENTS]}
          />
        </Box>

        <Divider />

        <SnapdocsAuditTrailBlock
          applicationId={applicationId}
          companyId={companyId}
          key={applicationId}
        />
      </>
    );
  };

  return (
    <PageBlock
      withAction
      actionContent={refreshButton}
      dataTestId={SnapdocsClosingSectionTestIds.SECTION}
      title={SECTION_TITLE}
    >
      {getContent()}
    </PageBlock>
  );
};

SnapdocsClosingSection.propTypes = {
  applicationId: PropTypes.string.isRequired,
  companyId: PropTypes.string,
};

export default memo(SnapdocsClosingSection);
